import React, { useState, useEffect, useRef , useMemo} from 'react';
// SVG Icons (unchanged)
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const UploadIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="17 8 12 3 7 8"></polyline>
    <line x1="12" y1="3" x2="12" y2="15"></line>
  </svg>
);
const FileIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
  </svg>
);
const TrashIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);
const PlusIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);
// New icons for maximize and minimize
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);
const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);
const API_URL = '/api';

const SearchableDropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select",
  error = false,
  width = "w-full",
  getOptionLabel = (option) => option.name || option.label || option.toString(),
  getOptionValue = (option) => option.value || option.id || option.toString(),
  getOptionSecondaryLabel = (option) => option.code || option.id || "",
  disabled = false,
  className = "",
  onMultiSelectTrigger = null,
  multiSelectShortcut = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownListRef = useRef(null);
  const searchInputRef = useRef(null);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  // Handle window resize and scroll to update position
  useEffect(() => {
    if (!isOpen) return;
    
    const handleUpdatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        });
      }
    };
    
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    
    return () => {
      window.removeEventListener('resize', handleUpdatePosition);
      window.removeEventListener('scroll', handleUpdatePosition);
    };
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Clear search term and reset highlighted index when opening
      setSearchTerm('');
      setHighlightedIndex(-1);
      
      // Focus the search input after a short delay to ensure it's rendered
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 0);
    }
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    
    return options.filter(option => {
      const label = getOptionLabel(option).toLowerCase();
      const secondaryLabel = getOptionSecondaryLabel(option).toLowerCase();
      
      return label.includes(term) || secondaryLabel.includes(term);
    });
  }, [options, searchTerm, getOptionLabel, getOptionSecondaryLabel]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Check for multi-select shortcut
    if (onMultiSelectTrigger && multiSelectShortcut && e.ctrlKey && e.key === multiSelectShortcut) {
      e.preventDefault();
      onMultiSelectTrigger();
      return;
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev < filteredOptions.length - 1) {
            return prev + 1;
          } else if (filteredOptions.length > 0) {
            return 0; // Loop back to first item
          }
          return prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev > 0) {
            return prev - 1;
          } else if (filteredOptions.length > 0) {
            return filteredOptions.length - 1; // Loop to last item
          }
          return prev;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleSelectOption = (option) => {
    onChange(getOptionValue(option));
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get selected option
  const selectedOption = options.find(option => getOptionValue(option) === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`${width} border rounded-lg px-3 py-2 cursor-pointer flex justify-between items-center ${
          error ? 'border-red-400' : 'border-[#E2E8F0]'
        } ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <span className="truncate">
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="p-2 border-b border-[#E2E8F0]">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          <div 
            className="max-h-60 overflow-y-auto" 
            ref={dropdownListRef}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={getOptionValue(option)}
                  className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] ${
                    highlightedIndex === index ? 'bg-[#BDDDFC]' : ''
                  } ${value === getOptionValue(option) ? 'bg-[#E6F0FF]' : ''}`}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="font-medium">{getOptionLabel(option)}</div>
                  {getOptionSecondaryLabel(option) && (
                    <div className="text-xs text-gray-500">{getOptionSecondaryLabel(option)}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No options found</div>
            )}
          </div>
        </div>
      )}
      
      {error && <span className="text-xs text-red-500">*</span>}
    </div>
  );
};


const initialFields = {
  vendor_code: '',
  vendor_name: '',
  vendor_type: '',
  country: '',
  BranchCode: '', // Add BranchCode field
  department_id: '', // Add department_id field
  status: 'Active',
  email: '', // Moved to vendor info
  account_name: '',
  account_number: '',
  ifsc_code: '',
  bank_name: '',
  bank_branch: '',
  logo: null,
  gst_certificate: null,
  company_reg_certificate: null,
  nda_contract_file: null
};
// ---------- Error Popup Component ----------
// ---------- Error Popup Component (replaced) ----------
const ErrorPopup = ({ show, messages = [], onClose }) => {
  // keyboard handler so Esc closes popup
  useEffect(() => {
    if (!show) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [show, onClose]);

  if (!show) return null;

  // helper to copy errors to clipboard
  const copyErrors = async () => {
    const text = messages.length ? messages.join('\n') : 'No details';
    try {
      await navigator.clipboard.writeText(text);
      // small visual feedback (replace with toast if you have one)
      // eslint-disable-next-line no-alert
      alert('Errors copied to clipboard');
    } catch {
      // eslint-disable-next-line no-alert
      alert('Copy failed — please select and copy manually.');
    }
  };

  // split messages to short pill vs long list (simple heuristic)
  const shortMessages = messages.filter(m => typeof m === 'string' && m.length <= 28);
  const longMessages = messages.filter(m => typeof m === 'string' && m.length > 28);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/45" onClick={onClose} />

      <div
        className="relative z-10 w-11/12 max-w-md bg-white rounded-2xl shadow-2xl p-6 transform transition-all"
        role="document"
        aria-labelledby="error-popup-title"
      >
        {/* top icon */}
        <div className="flex items-center justify-center -mt-10 mb-3">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center shadow-sm">
            <svg className="w-8 h-8 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        </div>

        {/* title + short summary */}
        <div className="text-center mb-3">
          <h2 id="error-popup-title" className="text-xl font-semibold text-red-600">Error</h2>
          <p className="text-sm text-gray-600 mt-1">
            {messages.length > 0 ? `${messages.length} problem${messages.length > 1 ? 's' : ''} found` : 'Something went wrong'}
          </p>
        </div>

        {/* pills for short messages */}
        {shortMessages.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center mb-3">
            {shortMessages.map((m, i) => (
              <span
                key={`pill-${i}`}
                className="text-xs px-3 py-1 bg-red-50 border border-red-100 text-red-700 rounded-full shadow-sm"
                title={m}
              >
                {m}
              </span>
            ))}
          </div>
        )}

        {/* divider */}
        <div className="border-t border-gray-100 mb-3" />

        {/* scrollable list for full messages (shows both long and remaining short ones) */}
        

        {/* actions */}
        <div className="flex justify-center items-center gap-3">
          <div className="flex items-center gap-2">
           
          

          <button
            onClick={onClose}
            className="px-5 py-2 bg-red-600 text-white rounded-full shadow hover:bg-red-700 focus:outline-none"
          >
            Try again
          </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// Helper function to check if a value is a File object
const isFile = (value) => value instanceof File;
// Main Vendor Form Modal Component
const VendorFormModal = ({ open, onClose, onSubmit, initialData, isEdit, loading }) => {
  const [fields, setFields] = useState(initialFields);
  const [errors, setErrors] = useState({});
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [vendorTypes, setVendorTypes] = useState([]);
  const [branches, setBranches] = useState([]); // Add branches state
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);
  const [isLoadingBranches, setIsLoadingBranches] = useState(true); // Add branches loading state
  const [filePreviews, setFilePreviews] = useState({});
  const [formLoading, setFormLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [activeTab, setActiveTab] = useState('vendorInfo');
    // Error popup state
  const [errorPopup, setErrorPopup] = useState({
    show: false,
    messages: []
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [isUserValid, setIsUserValid] = useState(false);
  const [authError, setAuthError] = useState(null);
  
  // New state for modal size
  const [isMaximized, setIsMaximized] = useState(false);
  
  // Address states
  const [addresses, setAddresses] = useState([]);
  const [addressErrors, setAddressErrors] = useState({});
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  
  // Contact states
  const [contacts, setContacts] = useState([]);
  const [contactErrors, setContactErrors] = useState({});
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  
  // Department-related state
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [screenConfig, setScreenConfig] = useState(null);
  const [showDepartmentField, setShowDepartmentField] = useState(false);
  
  const logoInputRef = useRef(null);
  const gstInputRef = useRef(null);
  const companyRegInputRef = useRef(null);
  const ndaInputRef = useRef(null);
  
  const tabs = [
    { id: 'vendorInfo', name: 'Vendor Info' },
    { id: 'contactInfo', name: 'Contact Info' },
    { id: 'addressInfo', name: 'Address' },
    { id: 'bankInfo', name: 'Bank Info' },
    { id: 'documents', name: 'Documents' },
  ];

  const getAuthHeaders = (isFormData = false) => {
  const token = localStorage.getItem('authToken'); // Or 'authToken' depending on your login logic
  
  const headers = {
    // Only add Authorization if token exists
    ...(token && { 'Authorization': `Bearer ${token}` })
  };

  // Only set Content-Type to 'application/json' if we are NOT sending FormData.
  // If sending FormData, the browser MUST set the Content-Type and boundary automatically.
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
};
  
  // Calculate form completion percentage
  const completionPercentage = Math.round(((currentStep + 1) / tabs.length) * 100);
  
  // Function to fetch current user
  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('authToken'); // store token after login
      if (!token) {
        setIsUserValid(false);
        setAuthError('No token found. Please log in again.');
        return false;
      }
      const userRes = await fetch(`${API_URL}/current-user`, {
        headers: getAuthHeaders(),
      });
      const userData = await userRes.json();
      if (userRes.ok && userData.user && userData.user.UserID) {
        setCurrentUser(userData.user);
        setIsUserValid(true);
        setAuthError(null);
        console.log('Current user fetched:', userData);
        return true;
      } else {
        console.error('Invalid user data received:', userData);
        setIsUserValid(false);
        setAuthError('User information not available. Please log in again.');
        return false;
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      setIsUserValid(false);
      setAuthError('Error fetching user information. Please refresh the page.');
      return false;
    }
  };
  
  // Function to fetch departments
  const fetchDepartments = async () => {
    setLoadingDepartments(true);
    try {
      const response = await fetch(`${API_URL}/departments`,{headers: getAuthHeaders()});
      const data = await response.json();
      console.log('Fetched departments:', data);
      setDepartments(data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };
  
  // Function to fetch screen configuration
  const fetchScreenConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/screens`,{headers: getAuthHeaders()});
      const data = await response.json();
      console.log('Fetched screen configurations:', data);
      
      // Find the vendor screen configuration
      const vendorScreen = data.screens.find(screen => 
        screen.ScreenName.toLowerCase() === 'VendorMaster'.toLowerCase()
      );
      console.log("Screen name is "+vendorScreen.ScreenName);
      
      console.log('Vendor screen configuration:', vendorScreen);
      if (vendorScreen) {
        setScreenConfig(vendorScreen);
        // Show department field if ShowOnHeader is 1
        const shouldShowDepartment = vendorScreen.ShowOnHeader === true;
        setShowDepartmentField(shouldShowDepartment);
        
        // If department field should be shown, fetch departments
        if (shouldShowDepartment) {
          fetchDepartments();
        }
      }
    } catch (error) {
      console.error('Error fetching screen configuration:', error);
      setShowDepartmentField(false);
    }
  };
  
  // Function to fetch vendor addresses
  const fetchVendorAddresses = async (vendorCode) => {
    try {
      setIsLoadingAddresses(true);
      console.log(`Fetching addresses for vendor: ${vendorCode}`);
      
      const response = await fetch(`${API_URL}/vendors/${vendorCode}/addresses`,{headers: getAuthHeaders()});
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const addressesData = await response.json();
      console.log('Fetched addresses:', addressesData);
      setAddresses(addressesData);
      return addressesData;
    } catch (error) {
      console.error('Error fetching vendor addresses:', error);
      setAddresses([]);
      return [];
    } finally {
      setIsLoadingAddresses(false);
    }
  };
  
  // Function to fetch vendor contacts
  const fetchVendorContacts = async (vendorCode) => {
    try {
      setIsLoadingContacts(true);
      console.log(`Fetching contacts for vendor: ${vendorCode}`);
      
      const response = await fetch(`${API_URL}/vendors/${vendorCode}/contacts`,{headers: getAuthHeaders()});
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const contactsData = await response.json();
      console.log('Fetched contacts:', contactsData);
      setContacts(contactsData);
      return contactsData;
    } catch (error) {
      console.error('Error fetching vendor contacts:', error);
      setContacts([]);
      return [];
    } finally {
      setIsLoadingContacts(false);
    }
  };
  
  // Function to fetch branches
  const fetchBranches = async () => {
    try {
      setIsLoadingBranches(true);
      const response = await fetch(`${API_URL}/branches`,{headers: getAuthHeaders()});
      const data = await response.json();
      setBranches(data);
    } catch (error) {
      console.error('Error fetching branches:', error);
      // If API fails, set some default branches
    } finally {
      setIsLoadingBranches(false);
    }
  };
  
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoadingTypes(true);
      try {
        const [typesRes] = await Promise.all([
          fetch(`${API_URL}/vendor-types`,{headers: getAuthHeaders()}),
          fetchCurrentUser(), // Fetch user data
          fetchBranches() // Fetch branches
        ]);
        const typesData = await typesRes.json();
        setVendorTypes(typesData.vendorTypes || []);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setIsLoadingTypes(false);
      }
    };
    fetchInitialData();
  }, []);
  
  useEffect(() => {
    if (open) {
      // Fetch current user when modal opens
      fetchCurrentUser();
      fetchBranches(); // Fetch branches when modal opens
      fetchScreenConfig(); // Fetch screen configuration to determine if department field should be shown
      
      if (isEdit && initialData) {
        // Add this logging to debug the issue
        console.log('Initial data received:', initialData);
        console.log('Addresses in initial data:', initialData.addresses);
        console.log('Contacts in initial data:', initialData.contacts);
        const departmentId = initialData.DepartmentID || initialData.department_id || '';
      console.log('Setting department_id to:', departmentId);
        
        const { created_by, updated_by, ...dataWithoutUserFields } = initialData;
        setFields({ 
          ...initialFields, 
          ...dataWithoutUserFields,
           department_id: departmentId.toString()|| '' // Add department_id to form data
        });
        setSelectedVendor(initialData);
        
        // Set addresses if they exist, otherwise fetch them
        if (initialData.addresses && initialData.addresses.length > 0) {
          setAddresses(initialData.addresses);
          console.log('Addresses set from initial data:', initialData.addresses);
        } else {
          console.log('No addresses in initial data, fetching separately');
          fetchVendorAddresses(initialData.vendor_code);
        }
        
        // Set contacts if they exist, otherwise fetch them
        if (initialData.contacts && initialData.contacts.length > 0) {
          setContacts(initialData.contacts);
          console.log('Contacts set from initial data:', initialData.contacts);
        } else {
          console.log('No contacts in initial data, fetching separately');
          fetchVendorContacts(initialData.vendor_code);
        }
        
        const previews = {};
        if (initialData.logo) previews.logo = initialData.logo;
        if (initialData.gst_certificate) previews.gst_certificate = initialData.gst_certificate;
        if (initialData.company_reg_certificate) previews.company_reg_certificate = initialData.company_reg_certificate;
        if (initialData.nda_contract_file) previews.nda_contract_file = initialData.nda_contract_file;
        setFilePreviews(previews);
      } else {
        setFields(initialFields);
        setFilePreviews({});
        setSelectedVendor(null);
        setAddresses([]);
        setContacts([]);
        setIsLoadingCode(true);
        fetch(`${API_URL}/vendorlatestcode`,{headers: getAuthHeaders()})
          .then(res => {
            if (!res.ok) {
              throw new Error(`HTTP error! status: ${res.status}`);
            }
            return res.json();
          })
          .then(data => {
            console.log('Received vendor code:', data);
            if (data && data.nextCode) {
              setFields(prev => ({ ...prev, vendor_code: data.nextCode }));
            } else {
              console.error('No nextCode in response:', data);
              setFields(prev => ({ ...prev, vendor_code: 'VAN-01' }));
            }
          })
          .catch(error => {
            console.error('Error fetching next vendor code:', error);
            setFields(prev => ({ ...prev, vendor_code: 'VAN-01' }));
          })
          .finally(() => {
            setIsLoadingCode(false);
          });
      }
      setErrors({});
      setAddressErrors({});
      setContactErrors({});
      setIsSubmitting(false);
      setCurrentStep(0);
      setActiveTab('vendorInfo');
      // Reset maximized state when modal opens
      setIsMaximized(false);
    }
  }, [open, initialData, isEdit]);
  
  const handleChange = e => {
    setFields({ ...fields, [e.target.name]: e.target.value });
  };
  
  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setFields(prev => ({
        ...prev,
        [fieldName]: file
      }));
      setFilePreviews(prev => ({
        ...prev,
        [fieldName]: URL.createObjectURL(file)
      }));
    }
  };
  
  const handleFileRemove = (fieldName, inputRef) => {
    setFields(prev => ({
      ...prev,
      [fieldName]: null
    }));
    setFilePreviews(prev => {
      const newPreviews = { ...prev };
      delete newPreviews[fieldName];
      return newPreviews;
    });
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };
  
  const getFilePreviewIcon = (preview) => {
    if (!preview) return null;
    if (typeof preview === 'string' && (preview.match(/\.(jpeg|jpg|gif|png)$/) || preview.startsWith('data:image'))) {
      return <img src={preview} alt="file preview" className="w-10 h-10 object-contain" />;
    }
    return <FileIcon className="text-[#6A89A7] text-xl" />;
  };
  
  const validate = () => {
      const errs = {};
  if (!fields.vendor_name) errs.vendor_name = 'Name Required';
  if (!fields.vendor_type) errs.vendor_type = 'Vendor Type Required';
  if (!fields.country) errs.country = 'Country Required';
  if (!fields.BranchCode) errs.BranchCode = 'Branch Code Required';
  if (!fields.email) errs.email = 'Email Required';
  if (!fields.status) errs.status = 'Status Required';
  if (showDepartmentField && !fields.department_id) errs.department_id = 'Department Required';

  setErrors(errs);

  const valid = Object.keys(errs).length === 0;
  if (!valid) {
    // compute errors now (use errs to avoid stale-state)
    const allErrors = getAllErrorsFrom(errs, addressErrors, contactErrors, fields, addresses, contacts);
    setErrorPopup({ show: true, messages: allErrors });
  }
  return valid;

  };
  
  const validateAddresses = () => {
    const errs = {};
    let isValid = true;
    
    addresses.forEach((address, index) => {
      errs[index] = {};
      if (!address.address_code) {
        errs[index].address_code = 'Address Code Required';
        isValid = false;
      }
      if (!address.address_line1) {
        errs[index].address_line1 = 'Address Required';
        isValid = false;
      }
      if (!address.city) {
        errs[index].city = 'City Required';
        isValid = false;
      }
      if (!address.state) {
        errs[index].state = 'State Required';
        isValid = false;
      }
      if (!address.zip_code) {
        errs[index].zip_code = 'Zip Code Required';
        isValid = false;
      }
      if (!address.country) {
        errs[index].country = 'Country Required';
        isValid = false;
      }
    });
    
      setAddressErrors(errs);
  if (!isValid) {
    const allErrors = getAllErrorsFrom(errors, errs, contactErrors, fields, addresses, contacts);
    setErrorPopup({ show: true, messages: allErrors });
  }
  return isValid;
};

  
  const validateContacts = () => {
    const errs = {};
    let isValid = true;
    
    // Check if at least one contact exists
    if (contacts.length === 0) {
      errs.general = 'At least one contact is required';
      isValid = false;
    }
    
    contacts.forEach((contact, index) => {
      errs[index] = {};
      if (!contact.contact_code) {
        errs[index].contact_code = 'Contact Code Required';
        isValid = false;
      }
      if (!contact.email) {
        errs[index].email = 'Email Required';
        isValid = false;
      }
      if (!contact.phone_number) {
        errs[index].phone_number = 'Phone Number Required';
        isValid = false;
      }
    });
    
     setContactErrors(errs);
  if (!isValid) {
    const allErrors = getAllErrorsFrom(errors, addressErrors, errs, fields, addresses, contacts);
    setErrorPopup({ show: true, messages: allErrors });
  }
  return isValid;
};

    // Create an errors array from provided error objects and current form state.
  const getAllErrorsFrom = (errsObj = errors, addrErrs = addressErrors, contErrs = contactErrors, fieldsLocal = fields, addressesLocal = addresses, contactsLocal = contacts) => {
    const allErrors = [];

    // header / field errors
    Object.entries(errsObj).forEach(([k, msg]) => {
      if (msg) allErrors.push(msg);
    });

    // addresses errors (each index)
    addressesLocal.forEach((addr, idx) => {
      const aErr = addrErrs[idx] || {};
      Object.entries(aErr).forEach(([k, msg]) => {
        if (msg) allErrors.push(`Address ${idx + 1}: ${msg}`);
      });
    });

    // contacts errors
    if (contErrs.general) allErrors.push(contErrs.general);
    contactsLocal.forEach((con, idx) => {
      const cErr = contErrs[idx] || {};
      Object.entries(cErr).forEach(([k, msg]) => {
        if (msg) allErrors.push(`Contact ${idx + 1}: ${msg}`);
      });
    });

    return allErrors;
  };

  // wrapper to use current state
  const getAllErrors = () => getAllErrorsFrom();

  
  const goToNextStep = () => {
    if (currentStep < tabs.length - 1) {
      setCurrentStep(prev => prev + 1);
      setActiveTab(tabs[currentStep + 1].id);
    }
  };
  
  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      setActiveTab(tabs[currentStep - 1].id);
    }
  };
  
  const saveCurrentStep = async () => {
    setFormLoading(true);
    
    try {
      // Simulate saving - in a real app, this would be an API call
      console.log(`Saving step ${currentStep + 1}: ${tabs[currentStep].name}`);
      
      // If this is the last step, submit the entire form
      if (currentStep === tabs.length - 1) {
        await handleSubmit();
      } else {
        // Move to next step
        goToNextStep();
      }
    } catch (error) {
      console.error('Error saving step:', error);
      alert('An error occurred while saving. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };
  
  // Address management functions
  const handleAddAddress = () => {
    const newAddress = {
      address_id: null,
      address_code: '', // Empty string for user to fill
      address_type: 'Registered',
      gst_number: '',
      pan_number: '',
      contact_person_name: '',
      contact_person_number: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      zip_code: '',
      country: fields.country || 'India',
    };
    setAddresses([...addresses, newAddress]);
  };
  
  const handleDeleteAddress = (index) => {
    const newAddresses = [...addresses];
    newAddresses.splice(index, 1);
    setAddresses(newAddresses);
    
    // Remove errors for this address
    const newErrors = { ...addressErrors };
    delete newErrors[index];
    setAddressErrors(newErrors);
  };
  
 const handleAddressChange = (index, field, value, event) => {
  const newAddresses = [...addresses];
  
  // For text inputs, preserve cursor position
  if (event && event.target && event.target.type === 'text') {
    const input = event.target;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    
    // Convert to uppercase
    const newValue = value;
    newAddresses[index] = { ...newAddresses[index], [field]: newValue };
    setAddresses(newAddresses);
    
    // After state update and re-render, restore cursor position
    requestAnimationFrame(() => {
      try {
        input.setSelectionRange(start, end);
      } catch (e) {
        console.debug('Could not set selection range:', e);
      }
    });
  } else {
    // For non-text inputs (like select)
    newAddresses[index] = { ...newAddresses[index], [field]: value };
    setAddresses(newAddresses);
  }
  
  // Clear error for this field if it exists
  if (addressErrors[index] && addressErrors[index][field]) {
    const newErrors = { ...addressErrors };
    delete newErrors[index][field];
    setAddressErrors(newErrors);
  }
};
  
  // Contact management functions
  const handleAddContact = () => {
    const newContact = {
      contact_id: null,
      contact_code: '', // Empty string for user to fill
      first_name: '',
      last_name: '',
      email: '',
      phone_number: ''
    };
    setContacts([...contacts, newContact]);
  };
  
  const handleDeleteContact = (index) => {
    const newContacts = [...contacts];
    newContacts.splice(index, 1);
    setContacts(newContacts);
    
    // Remove errors for this contact
    const newErrors = { ...contactErrors };
    delete newErrors[index];
    setContactErrors(newErrors);
  };
  
  const handleContactChange = (index, field, value, event) => {
    const newContacts = [...contacts];
    
    // For email inputs, we need special handling to preserve cursor position
    if (field === 'email' && event && event.target) {
      const input = event.target;
      const start = input.selectionStart;
      const end = input.selectionEnd;
      
      // Convert to uppercase
      const upperValue = value;
      newContacts[index] = { ...newContacts[index], [field]: upperValue };
      setContacts(newContacts);
      
      // After state update and re-render, restore cursor position
      requestAnimationFrame(() => {
        try {
          // Ensure the input still exists and is focused
          if (document.activeElement === input) {
            input.setSelectionRange(start, end);
          }
        } catch (e) {
          console.debug('Could not set selection range for email:', e);
        }
      });
    } 
    // For other text inputs, preserve cursor position
    else if (event && event.target && event.target.type === 'text') {
      const input = event.target;
      const start = input.selectionStart;
      const end = input.selectionEnd;
      
      // Convert to uppercase for text fields (except phone numbers)
      let newValue = value;
      if (field !== 'phone_number') {
        newValue = value;
      }
      
      newContacts[index] = { ...newContacts[index], [field]: newValue };
      setContacts(newContacts);
      
      // After state update and re-render, restore cursor position
      requestAnimationFrame(() => {
        try {
          input.setSelectionRange(start, end);
        } catch (e) {
          console.debug('Could not set selection range:', e);
        }
      });
    } else {
      // For non-text inputs
      let newValue = value;
      if (field !== 'phone_number') {
        newValue = value;
      }
      newContacts[index] = { ...newContacts[index], [field]: newValue };
      setContacts(newContacts);
    }
    
    // Clear error for this field if it exists
    if (contactErrors[index] && contactErrors[index][field]) {
      const newErrors = { ...contactErrors };
      delete newErrors[index][field];
      setContactErrors(newErrors);
    }
  };
  
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (isSubmitting || formLoading) {
      console.log('Form submission already in progress, ignoring duplicate submission');
      return;
    }
    
    if (!validate()) return;
    if (!validateAddresses()) return;
    if (!validateContacts()) return;
    
    // Check if we have a valid current user
    if (!isUserValid || !currentUser || !currentUser.UserID) {
      alert(authError || 'User information not available. Please refresh the page and log in again.');
      return;
    }
    
    setIsSubmitting(true);
    setFormLoading(true);
    
    try {
      console.log('Submitting form data:');
      
      // Create a new FormData object
      const formData = new FormData();
      
      // Append all non-file fields, but exclude created_by and updated_by
      Object.keys(fields).forEach(key => {
        if (key !== 'logo' && 
            key !== 'gst_certificate' && 
            key !== 'company_reg_certificate' && 
            key !== 'nda_contract_file' &&
            key !== 'created_by' && 
            key !== 'updated_by') {
          // Only add department_id if the field is shown
          if (key !== 'department_id' || showDepartmentField) {
            formData.append(key, fields[key]);
          }
        }
      });
      
      // Append file fields if they exist
      if (fields.logo) {
        formData.append('logo', fields.logo);
      }
      if (fields.gst_certificate) {
        formData.append('gst_certificate', fields.gst_certificate);
      }
      if (fields.company_reg_certificate) {
        formData.append('company_reg_certificate', fields.company_reg_certificate);
      }
      if (fields.nda_contract_file) {
        formData.append('nda_contract_file', fields.nda_contract_file);
      }
      
      // Append addresses as JSON (ensure it's properly serialized)
      const addressesJson = JSON.stringify(addresses);
      console.log('Sending addresses as JSON:', addressesJson);
      formData.append('addresses', addressesJson);
      
      // Append contacts as JSON
      const contactsJson = JSON.stringify(contacts);
      console.log('Sending contacts as JSON:', contactsJson);
      console.log('Contacts array:', contacts);
      // Log each contact's ID to verify they're preserved
      contacts.forEach((contact, index) => {
        console.log(`Contact ${index}: ID = ${contact.contact_id}, Type = ${typeof contact.contact_id}`);
      });
      formData.append('contacts', contactsJson);
      
      // Only append the relevant user ID field based on operation
      const userId = parseInt(currentUser.UserID);
      console.log('Using user ID:', userId);
      
      if (isEdit && selectedVendor) {
        formData.append('updated_by', userId);
        console.log('Appended updated_by:', userId);
      } else {
        formData.append('created_by', userId);
        console.log('Appended created_by:', userId);
      }
      
      // Log FormData contents for debugging
      console.log('FormData contents:');
      for (let pair of formData.entries()) {
        console.log(`${pair[0]}: ${pair[1]} (type: ${typeof pair[1]})`);
      }
      
      let response;
      if (isEdit && selectedVendor) {
        console.log('Updating vendor:', selectedVendor.vendor_code);
        response = await fetch(`${API_URL}/vendors/${selectedVendor.vendor_code}`, {
          method: 'PUT',
          body: formData,
          headers: getAuthHeaders(true)
        });
      } else {
        console.log('Creating new vendor');
        response = await fetch(`${API_URL}/vendors`, {
          method: 'POST',
          body: formData,
          headers: getAuthHeaders(true)
        });
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Server response:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        
        if (response.status === 409) {
          if (errorData.error === 'DUPLICATE_VENDOR_CODE') {
            console.log('Duplicate vendor code detected, refreshing code...');
            try {
              const codeResponse = await fetch(`${API_URL}/vendorlatestcode`,{headers: getAuthHeaders()});
              if (codeResponse.ok) {
                const codeData = await codeResponse.json();
                if (codeData && codeData.nextCode) {
                  setFields(prev => ({ ...prev, vendor_code: codeData.nextCode }));
                  
                  // Create a new FormData for retry
                  const retryFormData = new FormData();
                  
                  // Append all non-file fields
                  Object.keys(fields).forEach(key => {
                    if (key !== 'logo' && 
                        key !== 'gst_certificate' && 
                        key !== 'company_reg_certificate' && 
                        key !== 'nda_contract_file' &&
                        key !== 'created_by' && 
                        key !== 'updated_by') {
                      // Only add department_id if the field is shown
                      if (key !== 'department_id' || showDepartmentField) {
                        retryFormData.append(key, fields[key]);
                      }
                    }
                  });
                  
                  // Append vendor code
                  retryFormData.append('vendor_code', codeData.nextCode);
                  
                  // Append file fields
                  if (fields.logo) retryFormData.append('logo', fields.logo);
                  if (fields.gst_certificate) retryFormData.append('gst_certificate', fields.gst_certificate);
                  if (fields.company_reg_certificate) retryFormData.append('company_reg_certificate', fields.company_reg_certificate);
                  if (fields.nda_contract_file) retryFormData.append('nda_contract_file', fields.nda_contract_file);
                  
                  // Append addresses
                  retryFormData.append('addresses', JSON.stringify(addresses));
                  
                  // Append contacts
                  retryFormData.append('contacts', JSON.stringify(contacts));
                  
                  // Append the correct user ID for retry
                  retryFormData.append('created_by', userId);
                  
                  const retryResponse = await fetch(`${API_URL}/vendors`, {
                    method: 'POST',
                    body: retryFormData,
                    headers: getAuthHeaders()
                  });
                  
                  if (retryResponse.ok) {
                    const retryResult = await retryResponse.json();
                    console.log('Retry successful:', retryResult);
                    onSubmit(retryFormData);
                    onClose();
                    return;
                  } else {
                    const retryErrorData = await retryResponse.json();
                    throw new Error(retryErrorData.message || 'Failed to create vendor after retry');
                  }
                }
              }
            } catch (retryError) {
              console.error('Error during retry:', retryError);
              throw new Error('Failed to create vendor after retry. Please try again.');
            }
          } else if (errorData.error === 'DUPLICATE_ADDRESS_CODE') {
  setErrorPopup({
    show: true,
    messages: [errorData.message || 'An address code already exists. Please use a unique address code.']
  });
  return; // stop further execution
} else if (errorData.error === 'DUPLICATE_CONTACT_CODE') {
  setErrorPopup({
    show: true,
    messages: [errorData.message || 'A contact code already exists. Please use a unique contact code.']
  });
  return; // stop further execution
}

        }
        
        throw new Error(errorData.message || `Failed to ${isEdit ? 'update' : 'create'} vendor`);
      }
      
      const result = await response.json();
      console.log('Server response:', result);
      
      onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error saving vendor:', error);

      // Build message(s) - if error object contains structured info prefer that
      let msgs = [];
      if (error && error.message) msgs.push(error.message);
      if (error && error.response && error.response.message) msgs.push(error.response.message);

      if (msgs.length === 0) msgs.push('An error occurred while saving the vendor');

      setErrorPopup({ show: true, messages: msgs });
    } finally {
      setFormLoading(false);
      setIsSubmitting(false);
    }

  };
  
  // Toggle maximize/minimize
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
 return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ease-in-out
        ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
    >
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .custom-scrollbar::-webkit-scrollbar {
            width: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
          .table-container {
            width: 100%;
            overflow-x: auto;
            padding-bottom: 16px; /* Space for scrollbar */
            margin-bottom: 16px; /* Space below the table */
          }
          .data-table {
            width: 100%;
            border-collapse: collapse;
            min-width: 1200px; /* Ensure table has a minimum width */
          }
          .data-table th, .data-table td {
            padding: 12px;
            text-align: left;
            border-bottom: 1px solid #e2e8f0;
            min-width: 120px; /* Minimum width for each cell */
          }
          .data-table th {
            background-color: #f8fafc;
            font-weight: 600;
            color: #475569;
            font-size: 0.75rem;
            text-transform: uppercase;
            position: sticky;
            top: 0;
          }
          .data-table input, .data-table select {
            width: 100%;
            padding: 8px 10px;
            border: 1px solid #e2e8f0;
            border-radius: 4px;
            font-size: 0.875rem;
          }
          .data-table input:focus, .data-table select:focus {
            outline: none;
            border-color: #6a89a7;
            box-shadow: 0 0 0 2px rgba(106, 137, 167, 0.2);
          }
          .data-table .error {
            border-color: #ef4444;
          }
          .error-message {
            color: #ef4444;
            font-size: 0.75rem;
            margin-top: 4px;
          }
          .table-actions {
            min-width: 80px;
          }
          .table-actions button {
            display: flex;
            align-items: center;
            justify-content: center;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div 
        className={`relative bg-white shadow-2xl flex flex-col 
          transition-transform transform-gpu duration-300 ease-in-out
          ${open ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}
          ${isMaximized ? 'w-full h-full max-w-none m-0 rounded-none' : 'w-full max-w-6xl h-full rounded-none'}`}
      >
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">{isEdit ? 'Edit Vendor' : 'Create New Vendor'}</h2>
            <div className="flex items-center space-x-2">
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
              </button>
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                onClick={onClose}
                title="Close"
              >
                <CloseIcon size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* User validation warning */}
        {open && !isUserValid && (
          <div className="mx-8 mb-4 bg-red-50 border-l-4 border-red-500 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">
                  {authError || 'User information not available. Please refresh the page and log in again.'}
                </p>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex-grow overflow-y-auto custom-scrollbar px-8">
          <form onSubmit={handleSubmit} className="space-y-6 pb-6">
            {/* Vendor Information Section */}
            <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <div className='space-y-4'>
                <h3 className="text-lg font-semibold text-[#384959] mb-4">Vendor Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className='flex flex-col gap-2'>
                    <label className="block text-sm font-medium text-[#64748b]">Vendor Code</label>
                    <input 
                      name="vendor_code" 
                      value={isLoadingCode ? 'Loading...' : fields.vendor_code} 
                      readOnly 
                      className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] text-[#64748b] border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]" 
                    />
                  </div>
                  <div className='flex flex-col gap-2'>
                    <label className="block text-sm font-medium text-[#64748b]">Name *</label>
                    <input 
                      name="vendor_name" 
                      autoComplete="off"
                      value={fields.vendor_name} 
                      onChange={handleChange} 
                      className={`w-full border rounded-lg px-3 py-2 ${errors.vendor_name ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase`} 
                    />
                    {errors.vendor_name && <span className="text-xs text-red-500">{errors.vendor_name}</span>}
                  </div>
                  <div className='flex flex-col gap-2'>
                    <label className="block text-sm font-medium text-[#64748b]">Type *</label>
                     <SearchableDropdown
    options={vendorTypes}
    value={fields.vendor_type}
    onChange={(value) => handleChange({ target: { name: 'vendor_type', value } })}
    getOptionLabel={(type) => type.type_name}
    getOptionValue={(type) => type.type_name}
    placeholder={isLoadingTypes ? 'Loading vendor types...' : 'Select Vendor Type'}
    error={errors.vendor_type}
    disabled={isLoadingTypes}
    className="uppercase"
  />
                    {errors.vendor_type && <span className="text-xs text-red-500">{errors.vendor_type}</span>}
                  </div>
                  <div className='flex flex-col gap-2'>
                    <label className="block text-sm font-medium text-[#64748b]">Country *</label>
                    <input name="country" autoComplete="off" value={fields.country} onChange={handleChange} className={`w-full border rounded-lg px-3 py-2 ${errors.country ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase`} />
                    {errors.country && <span className="text-xs text-red-500">{errors.country}</span>}
                  </div>
                  {/* Add Branch Selection Field */}
                  <div className='flex flex-col gap-2'>
                    <label className="block text-sm font-medium text-[#64748b]">Branch *</label>
                     <SearchableDropdown
    options={branches}
    value={fields.BranchCode}
    onChange={(value) => handleChange({ target: { name: 'BranchCode', value } })}
    getOptionLabel={(branch) => `${branch.BranchCode} - ${branch.BranchName}`}
    getOptionValue={(branch) => branch.BranchCode}
    getOptionSecondaryLabel={(branch) => branch.BranchName}
    placeholder={isLoadingBranches ? 'Loading branches...' : 'Select Branch'}
    error={errors.BranchCode}
    disabled={isLoadingBranches}
    className="uppercase"
  />
                    {errors.BranchCode && <span className="text-xs text-red-500">{errors.BranchCode}</span>}
                  </div>
                  
                  {/* Department Selection Field - Only shown if ShowOnHeader is 1 */}
                  {showDepartmentField && (
                    <div className='flex flex-col gap-2'>
                      <label className="block text-sm font-medium text-[#64748b]">Department *</label>
                          <SearchableDropdown
      options={departments}
      value={fields.department_id}
      onChange={(value) => handleChange({ target: { name: 'department_id', value } })}
      getOptionLabel={(dept) => `${dept.DepartmentCode} - ${dept.DepartmentName}`}
      getOptionValue={(dept) => dept.DepartmentID.toString()}
      getOptionSecondaryLabel={(dept) => dept.DepartmentName}
      placeholder={loadingDepartments ? 'Loading departments...' : 'Select Department'}
      error={errors.department_id}
      disabled={loadingDepartments}
      className="uppercase"
    />
                      {errors.department_id && <span className="text-xs text-red-500">{errors.department_id}</span>}
                    </div>
                  )}
                  
                  <div className='flex flex-col gap-2'>
                    <label className="block text-sm font-medium text-[#64748b]">Status *</label>
                    <select name="status" value={fields.status} onChange={handleChange} className={`w-full border rounded-lg px-3 py-2 ${errors.status ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase`}>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                    {errors.status && <span className="text-xs text-red-500">{errors.status}</span>}
                  </div>
                  <div className='flex flex-col gap-2'>
                    <label className="block text-sm font-medium text-[#64748b]">Email *</label>
                    <input name="email" value={fields.email} autoComplete="off" onChange={handleChange} className={`w-full border rounded-lg px-3 py-2 ${errors.email ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase`} />
                    {errors.email && <span className="text-xs text-red-500">{errors.email}</span>}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Contact Information Section */}
            {/* Contact Information Section */}
            <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <div className='space-y-4'>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#384959]">Contact Information</h3>
                  <button
                    type="button"
                    className="flex items-center gap-2 px-4 py-2 bg-[#6A89A7] text-white rounded-lg hover:bg-[#88BDF2] transition-colors"
                    onClick={handleAddContact}
                  >
                    <PlusIcon /> Add Contact
                  </button>
                </div>
                
                {contactErrors.general && (
                  <div className="text-red-500 text-sm mb-2">{contactErrors.general}</div>
                )}
                
                {isLoadingContacts ? (
                  <div className="text-center py-8 text-[#64748b]">
                    <p>Loading contacts...</p>
                  </div>
                ) : contacts.length > 0 ? (
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Contact Code *</th>
                          <th>First Name</th>
                          <th>Last Name</th>
                          <th>Email *</th>
                          <th>Phone Number *</th>
                          <th className="table-actions">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contacts.map((contact, index) => (
                          <tr key={contact.contact_id || index}>
                            <td>
                              <input
                                type="text"
                                value={contact.contact_code || ''}
                                onChange={(e) => handleContactChange(index, 'contact_code', e.target.value, e)}
                                className={`${contactErrors[index]?.contact_code ? 'error' : ''}`}
                                placeholder="Enter unique code"
                              />
                              {contactErrors[index]?.contact_code && (
                                <div className="error-message">{contactErrors[index].contact_code}</div>
                              )}
                            </td>
                            <td>
                              <input
                                type="text"
                                value={contact.first_name || ''}
                                onChange={(e) => handleContactChange(index, 'first_name', e.target.value, e)}
                                style={{ textTransform: 'uppercase' }}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                value={contact.last_name || ''}
                                onChange={(e) => handleContactChange(index, 'last_name', e.target.value, e)}
                                 style={{ textTransform: 'uppercase' }}
                              />
                            </td>
                            {/* Contact Information Section - Email Input */}
                            <td>
                              <input
                                type="email"
                                value={contact.email || ''}
                                onChange={(e) => handleContactChange(index, 'email', e.target.value, e)}
                                className={`${contactErrors[index]?.email ? 'error' : ''}`}
                                autoComplete="email"
                                 style={{ textTransform: 'uppercase' }}
                              />
                              {contactErrors[index]?.email && (
                                <div className="error-message">{contactErrors[index].email}</div>
                              )}
                            </td>
                            <td>
                              <input
                                type="text"
                                value={contact.phone_number || ''}
                                onChange={(e) => handleContactChange(index, 'phone_number', e.target.value, e)}
                                className={`${contactErrors[index]?.phone_number ? 'error' : ''}`}
                                 style={{ textTransform: 'uppercase' }}
                              />
                              {contactErrors[index]?.phone_number && (
                                <div className="error-message">{contactErrors[index].phone_number}</div>
                              )}
                            </td>
                            <td className="table-actions">
                              <button
                                type="button"
                                className="text-red-600 hover:text-red-800 p-1"
                                onClick={() => handleDeleteContact(index)}
                              >
                                <TrashIcon />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-[#64748b]">
                    <p>No contacts added yet. Click "Add Contact" to add one.</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Address Information Section */}
            {/* Address Information Section */}
            <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <div className='space-y-4'>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#384959]">Address Information</h3>
                  <button
                    type="button"
                    className="flex items-center gap-2 px-4 py-2 bg-[#6A89A7] text-white rounded-lg hover:bg-[#88BDF2] transition-colors"
                    onClick={handleAddAddress}
                  >
                    <PlusIcon /> Add Address
                  </button>
                </div>
                
                {isLoadingAddresses ? (
                  <div className="text-center py-8 text-[#64748b]">
                    <p>Loading addresses...</p>
                  </div>
                ) : addresses.length > 0 ? (
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Address Code *</th>
                          <th>Type</th>
                          <th>Address Line 1</th>
                          <th>City</th>
                          <th>State</th>
                          <th>Zip Code</th>
                          <th>Country</th>
                          <th>GST Number</th>
                          <th>PAN Number</th>
                          <th>Contact Person</th>
                          <th>Contact Number</th>
                          <th className="table-actions">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {addresses.map((address, index) => (
                          <tr key={address.address_id || index}>
                            <td>
                              <input
                                type="text"
                                value={address.address_code || ''}
                                onChange={(e) => handleAddressChange(index, 'address_code', e.target.value, e)}
                                className={`${addressErrors[index]?.address_code ? 'error' : ''}`}
                                placeholder="Enter unique code"
                                 style={{ textTransform: 'uppercase' }}
                              />
                              {addressErrors[index]?.address_code && (
                                <div className="error-message">{addressErrors[index].address_code}</div>
                              )}
                            </td>
                            <td>
                              <select
                                value={address.address_type || ''}
                                onChange={(e) => handleAddressChange(index, 'address_type', e.target.value, e)}
                              >
                                <option value="Registered">Registered</option>
                                <option value="Billing">Billing</option>
                                <option value="Shipping">Shipping</option>
                                <option value="Office">Office</option>
                                <option value="Warehouse">Warehouse</option>
                                <option value="Other">Other</option>
                              </select>
                            </td>
                            <td>
                              <input
                                type="text"
                                value={address.address_line1 || ''}
                                onChange={(e) => handleAddressChange(index, 'address_line1', e.target.value, e)}
                                className={`${addressErrors[index]?.address_line1 ? 'error' : ''}`}
                                 style={{ textTransform: 'uppercase' }}
                              />
                              {addressErrors[index]?.address_line1 && (
                                <div className="error-message">{addressErrors[index].address_line1}</div>
                              )}
                            </td>
                            <td>
                              <input
                                type="text"
                                value={address.city || ''}
                                onChange={(e) => handleAddressChange(index, 'city', e.target.value, e)}
                                className={`${addressErrors[index]?.city ? 'error' : ''}`}
                                 style={{ textTransform: 'uppercase' }}
                              />
                              {addressErrors[index]?.city && (
                                <div className="error-message">{addressErrors[index].city}</div>
                              )}
                            </td>
                            <td>
                              <input
                                type="text"
                                value={address.state || ''}
                                onChange={(e) => handleAddressChange(index, 'state', e.target.value, e)}
                                className={`${addressErrors[index]?.state ? 'error' : ''}`}
                                 style={{ textTransform: 'uppercase' }}
                              />
                              {addressErrors[index]?.state && (
                                <div className="error-message">{addressErrors[index].state}</div>
                              )}
                            </td>
                            <td>
                              <input
                                type="text"
                                value={address.zip_code || ''}
                                onChange={(e) => handleAddressChange(index, 'zip_code', e.target.value, e)}
                                className={`${addressErrors[index]?.zip_code ? 'error' : ''}`}
                                 style={{ textTransform: 'uppercase' }}
                              />
                              {addressErrors[index]?.zip_code && (
                                <div className="error-message">{addressErrors[index].zip_code}</div>
                              )}
                            </td>
                            <td>
                              <input
                                type="text"
                                value={address.country || ''}
                                onChange={(e) => handleAddressChange(index, 'country', e.target.value, e)}
                                className={`${addressErrors[index]?.country ? 'error' : ''}`}
                                 style={{ textTransform: 'uppercase' }}
                              />
                              {addressErrors[index]?.country && (
                                <div className="error-message">{addressErrors[index].country}</div>
                              )}
                            </td>
                            <td>
                              <input
                                type="text"
                                value={address.gst_number || ''}
                                onChange={(e) => handleAddressChange(index, 'gst_number', e.target.value, e)}
                                 style={{ textTransform: 'uppercase' }}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                value={address.pan_number || ''}
                                onChange={(e) => handleAddressChange(index, 'pan_number', e.target.value, e)}
                                 style={{ textTransform: 'uppercase' }}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                value={address.contact_person_name || ''}
                                onChange={(e) => handleAddressChange(index, 'contact_person_name', e.target.value, e)}
                                 style={{ textTransform: 'uppercase' }}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                value={address.contact_person_number || ''}
                                onChange={(e) => handleAddressChange(index, 'contact_person_number', e.target.value, e)}
                              />
                            </td>
                            <td className="table-actions">
                              <button
                                type="button"
                                className="text-red-600 hover:text-red-800 p-1"
                                onClick={() => handleDeleteAddress(index)}
                              >
                                <TrashIcon />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-[#64748b]">
                    <p>No addresses added yet. Click "Add Address" to add one.</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Bank Information Section */}
            <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <div className='space-y-4'>
                <h3 className="text-lg font-semibold text-[#384959] mb-4">Bank Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className='flex flex-col gap-2'>
                    <label className="block text-sm font-medium text-[#64748b]">Account No.</label>
                    <input name="account_number" autoComplete="off" value={fields.account_number} onChange={handleChange} className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase" />
                  </div>
                  <div className='flex flex-col gap-2'>
                    <label className="block text-sm font-medium text-[#64748b]">Account Name</label>
                    <input name="account_name" autoComplete="off" value={fields.account_name} onChange={handleChange} className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase" />
                  </div>
                  <div className='flex flex-col gap-2'>
                    <label className="block text-sm font-medium text-[#64748b]">IFSC Code</label>
                    <input name="ifsc_code" autoComplete="off" value={fields.ifsc_code} onChange={handleChange} className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase" />
                  </div>
                  <div className='flex flex-col gap-2'>
                    <label className="block text-sm font-medium text-[#64748b]">Bank Name</label>
                    <input name="bank_name" autoComplete="off" value={fields.bank_name} onChange={handleChange} className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase" />
                  </div>
                  <div className='flex flex-col gap-2'>
                    <label className="block text-sm font-medium text-[#64748b]">Bank Branch</label>
                    <input name="bank_branch" autoComplete="off" value={fields.bank_branch} onChange={handleChange} className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase" />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Documents Section */}
            <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <div className='space-y-4'>
                <h3 className="text-lg font-semibold text-[#384959] mb-4">Documents</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {['logo', 'gst_certificate', 'company_reg_certificate', 'nda_contract_file'].map(fieldName => (
                    <div key={fieldName} className='flex flex-col gap-2'>
                      <label className="block text-sm font-medium text-[#64748b]">{
                        {
                          logo: 'Company Logo',
                          gst_certificate: 'GST Certificate',
                          company_reg_certificate: 'Company Registration',
                          nda_contract_file: 'NDA Contract'
                        }[fieldName]
                      }</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="file"
                          accept={fieldName === 'logo' ? 'image/*' : '.pdf,.doc,.docx'}
                          onChange={(e) => handleFileChange(e, fieldName)}
                          className="hidden"
                          id={`${fieldName}-upload`}
                          ref={
                            fieldName === 'logo' ? logoInputRef :
                            fieldName === 'gst_certificate' ? gstInputRef :
                            fieldName === 'company_reg_certificate' ? companyRegInputRef :
                            ndaInputRef
                          }
                        />
                        <label
                          htmlFor={`${fieldName}-upload`}
                          className="flex items-center gap-2 px-4 py-2 bg-[#6A89A7] text-white rounded-lg cursor-pointer hover:bg-[#88BDF2] shadow transition-colors"
                        >
                          <UploadIcon /> Upload
                        </label>
                        {filePreviews[fieldName] && (
                          <div className="flex items-center gap-2">
                            {getFilePreviewIcon(filePreviews[fieldName])}
                            <button
                              type="button"
                              onClick={() => handleFileRemove(fieldName,
                                fieldName === 'logo' ? logoInputRef :
                                fieldName === 'gst_certificate' ? gstInputRef :
                                fieldName === 'company_reg_certificate' ? companyRegInputRef :
                                ndaInputRef
                              )}
                              className="p-1 text-red-500 hover:text-red-700 transition-colors"
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        )}
                        {isEdit && initialData?.[fieldName] && !isFile(fields[fieldName]) && !filePreviews[fieldName] && (
                          <div className="text-sm text-[#64748b]">
                            {typeof initialData[fieldName] === 'string' ? 
                              initialData[fieldName].split('/').pop() : 
                              'Existing file'}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </form>
        </div>
        
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button 
              type="button" 
              className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" 
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              type="submit"
              form="vendor-form"
              className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#88BDF2] disabled:opacity-50 disabled:cursor-not-allowed" 
              disabled={formLoading || isSubmitting || isLoadingCode || !isUserValid}
              onClick={handleSubmit}
            >
              {formLoading || isSubmitting ? 'Saving...' : (isEdit ? 'Update Vendor' : 'Create Vendor')}
            </button>
          </div>
        </div>
      </div>
            {/* Error popup */}
      <ErrorPopup
        show={errorPopup.show}
        messages={errorPopup.messages}
        onClose={() => setErrorPopup({ show: false, messages: [] })}
      />

    </div>
    
  );
};
export default VendorFormModal;